import { createContext, useContext } from 'react';
import { GuestFormResponse } from './api-types';

/**
 * Where the magic link landed. Drives the page: `loading` → spinner, `active` → the runner,
 * everything else → a screen that explains what happened and what to do about it.
 *
 * These are deliberately separate states rather than one generic error: "the form was closed" and
 * "this link never worked" call for different words and different next steps, and collapsing them
 * would leave the recipient guessing.
 */
export type GuestFormState =
  | 'loading'
  | 'active'
  | 'submitted' // already answered — terminal, and a success rather than a failure
  | 'expired' // the invitation window is over — a new link is needed
  | 'revoked' // an operator withdrew this invitation
  | 'closed' // the form itself stopped accepting answers
  | 'replaced' // the version being answered was withdrawn mid-fill
  | 'invalid' // a token was present but the server rejected it
  | 'missing-token' // no token in the URL, e.g. a bookmarked address
  | 'unavailable' // feature flag off (404)
  | 'needs-update'; // the definition uses a block this app build cannot render

export interface GuestFormSession {
  /** In-memory bearer token; null until redeemed, and never persisted. */
  sessionToken: string | null;
  /** Which screen the page should show. */
  state: GuestFormState;
  /** The redeem payload: definition, saved answers, title. Seeds the runner. */
  data: GuestFormResponse | null;
  /** Flip to the success screen after a successful submit. */
  markSubmitted: () => void;
  /** Flip to the "your app is too old to render this" screen. */
  markNeedsUpdate: () => void;
  /**
   * The freshest session token, read at call time rather than at render time.
   *
   * `sessionToken` above is fine for rendering, but not for the second half of an async sequence:
   * a recovery that just rotated the token publishes it through state, and a callback that started
   * before that render still closes over the old one. Submitting is exactly that sequence — a save
   * that can recover, then a submit — so it reads the token from here.
   */
  getSessionToken: () => string | null;
  /**
   * Re-redeem the link token for a fresh session WITHOUT reseeding the form — for when a write
   * returns 401 mid-answer because the short session lapsed while the invitation is still valid.
   * Returns the fresh token so the failed write can retry, or null when the invitation is
   * genuinely dead (in which case the matching screen has already been selected). Single-flight.
   */
  recoverSession: () => Promise<string | null>;
  /** Route a write that hit a dead invitation to the screen that explains it. */
  routeError: (
    error: unknown,
    stage: 'autosave' | 'submit' | 'language-switch'
  ) => void;
}

export const GuestFormSessionContext = createContext<
  GuestFormSession | undefined
>(undefined);

export const useGuestFormSession = (): GuestFormSession => {
  const context = useContext(GuestFormSessionContext);
  if (!context) {
    throw new Error(
      'useGuestFormSession must be used within a GuestFormSessionProvider'
    );
  }
  return context;
};
